import { useQuery } from "@tanstack/react-query";
import { History, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import {
  Button,
  DataTable,
  ErrorState,
  Field,
  Input,
  FilterBar,
  PageHeader,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import type { PageResponse, TeamSummary } from "../../shared/types/api";
import { TeamFormDrawer } from "./TeamFormDrawer";

const TEAM_TYPE_LABELS: Record<string, string> = {
  HOME_TEAM: "Home",
  DELIVERY_TEAM: "Delivery",
  TEMPORARY_TEAM: "Temporary",
  FUNCTIONAL_TEAM: "Functional",
};

const today = () => new Date().toISOString().slice(0, 10);

/** Ten a page to begin with; the reader can ask for more. */
const DEFAULT_PAGE_SIZE = "10";

/* Outside the component so its identity is stable — useUrlFilters memoises on it. */
const TEAM_FILTERS = { q: "", page: "0", size: DEFAULT_PAGE_SIZE };

/**
 * The team list.
 *
 * <p>Headcount is shown as of a date, and the date control sits beside the numbers rather than
 * inside a filter menu. That placement is the point: the number means nothing without the date it
 * describes, and keeping them together is what stops a historical figure being read as current.
 *
 * <p>Searched and paged, because an organisation outgrows one screenful of teams. It used to ask
 * for the first hundred by name and draw no pager at all, so the hundred-and-first team existed
 * in the database and nowhere a person could reach it — and nothing on screen admitted that
 * anything had been left out.
 */
export function TeamsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [asOf, setAsOf] = useState(today);
  const [creating, setCreating] = useState(false);

  /* In the URL, so opening a team and coming back returns the list as it was left. */
  const [filters, setFilters] = useUrlFilters(TEAM_FILTERS);
  const { q: search } = filters;
  const page = Number(filters.page) || 0;
  const pageSize = Number(filters.size) || Number(DEFAULT_PAGE_SIZE);

  const teams = useQuery({
    queryKey: ["teams", asOf, search, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        asOf,
        page: String(page),
        size: String(pageSize),
        sort: "name,asc",
      });
      if (search.trim()) {
        params.set("q", search.trim());
      }
      return api.get<PageResponse<TeamSummary>>(`/api/v1/teams?${params.toString()}`);
    },
  });

  const isHistorical = asOf !== today();

  const columns: Column<TeamSummary>[] = [
    {
      key: "code",
      header: "Code",
      render: (team) => <span className="font-mono text-xs text-ink-500">{team.code}</span>,
    },
    {
      key: "name",
      header: "Name",
      render: (team) => <span className="font-medium text-ink-900">{team.name}</span>,
    },
    {
      key: "teamType",
      header: "Type",
      render: (team) => (
        <StatusBadge tone="blue">{TEAM_TYPE_LABELS[team.teamType] ?? team.teamType}</StatusBadge>
      ),
    },
    { key: "teamLeadName", header: "Lead", render: (team) => team.teamLeadName ?? "—" },
    {
      key: "memberCount",
      header: `Members on ${asOf}`,
      className: "text-right tnum",
      render: (team) => team.memberCount,
    },
    {
      key: "status",
      header: "Status",
      render: (team) => (
        <StatusBadge tone={team.status === "ACTIVE" ? "green" : "gray"}>{team.status}</StatusBadge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Teams"
        subtitle="An employee can belong to several teams at once, and membership is kept with the dates it applied."
      />

      <FilterBar actions={
          can("TEAM_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              New team
            </Button>
          )
        }>
          <div className="min-w-56">
            <Field label="Search">
              <Input
                placeholder="Team or code"
                value={search}
                /* Every search returns to the first page: page three of a wider result is empty
                   under a narrower one, and that reads as "nothing matches". */
                onChange={(event) => setFilters({ q: event.target.value, page: "0" })}
              />
            </Field>
          </div>

          <div className="w-52">
            <Field label="Membership as of">
              <Input
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
              />
            </Field>
          </div>

          {isHistorical && (
            // Called out prominently. A historical view that looks identical to the current one is
            // how a past figure ends up quoted as today's.
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <History className="h-4 w-4 shrink-0" />
              Showing membership as it was on {asOf}
            </div>
          )}

          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={() => setAsOf(today())}>
              Back to today
            </Button>
          </div>
      </FilterBar>

      {teams.isError ? (
        <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={teams.data?.content ?? []}
          rowKey={(team) => team.id}
          loading={teams.isPending}
          page={page}
          pageSize={pageSize}
          totalElements={teams.data?.totalElements ?? 0}
          onPageChange={(next) => setFilters({ page: String(next) })}
          onPageSizeChange={(next) => setFilters({ size: String(next), page: "0" })}
          onRowClick={(team) => navigate(`/teams/${team.id}?asOf=${asOf}`)}
          emptyTitle={
            search.trim()
              ? "Nothing matches that search"
              : isHistorical
                ? `No teams existed on ${asOf}`
                : "No teams yet"
          }
          emptyMessage={
            search.trim()
              ? "No team here has that in its name or its code."
              : isHistorical
                ? "Teams are kept once created, so this is the answer for that date."
                : "Teams group people for delivery. Create one to start recording who worked where, and when."
          }
          emptyAction={
            can("TEAM_MANAGE") &&
            !isHistorical && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                Create the first team
              </Button>
            )
          }
        />
      )}

      <TeamFormDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => void teams.refetch()}
      />
    </>
  );
}
